import { useEffect, useState } from "react";
import axios from "axios";
import { Download, QrCode } from "lucide-react";
import { API, money } from "../lib/pos";
import { DateRangeField } from "../components/DateField";

export function Manager({ orders, stations }) {
  const [discountLog, setDiscountLog] = useState(null);
  const [cancelLog, setCancelLog] = useState(null);
  const [qrStats, setQrStats] = useState(null);
  const [orderQrStats, setOrderQrStats] = useState(null);
  // Independent date-range filters for each audit CSV export
  const today = new Date().toISOString().slice(0, 10);
  const [dStart, setDStart] = useState(today);
  const [dEnd, setDEnd] = useState(today);
  const [cStart, setCStart] = useState(today);
  const [cEnd, setCEnd] = useState(today);
  const downloadCsv = async (kind, start, end) => {
    try {
      const r = await axios.get(`${API}/${kind}/export.csv`, { params: { start, end }, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `${kind}_${start === end ? start : `${start}_to_${end}`}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (e) { alert(`Couldn't download ${kind} CSV.`); }
  };
  useEffect(() => {
    axios.get(`${API}/discounts`).then((r) => setDiscountLog(r.data)).catch(() => setDiscountLog(null));
    axios.get(`${API}/cancellations`).then((r) => setCancelLog(r.data)).catch(() => setCancelLog(null));
    axios.get(`${API}/qr/stats?days=14&kind=display`).then((r) => setQrStats(r.data)).catch(() => setQrStats(null));
    axios.get(`${API}/qr/stats?days=14&kind=order`).then((r) => setOrderQrStats(r.data)).catch(() => setOrderQrStats(null));
  }, [orders.length]);

  const activeStations = stations.filter((s) => s.active !== false);
  // Only count revenue/items belonging to a category served by an active
  // station. Disabling a station retroactively removes its items from every
  // manager metric (Net sales, tickets served, station performance).
  // Note: a station with NO assigned_categories is treated as "Expo / all"
  // and does NOT act as a whitelist gate.
  const activeCats = new Set();
  let hasCatchAll = false;
  activeStations.forEach((s) => {
    const cats = s.assigned_categories || [];
    if (!cats.length) hasCatchAll = true;
    cats.forEach((c) => activeCats.add(c));
  });
  const isActiveCat = (cat) => hasCatchAll || activeCats.has(cat);
  const effQty = (i) => Math.max(0, (i.quantity || 0) - (i.cancelled_quantity || 0));
  const orderActiveRevenue = (o) => (o.items || [])
    .filter((i) => isActiveCat(i.category))
    .reduce((a, i) => a + (i.price || 0) * effQty(i), 0);
  // A ticket counts as revenue-earning if it was ever paid AND wasn't voided.
  // Additionally it must have at least ₹1 of items on an active station.
  const revenueOrders = orders.filter((x) => x.paid_at && x.status !== "cancelled" && orderActiveRevenue(x) > 0);
  const openOrders = orders.filter((x) => !x.paid_at && x.status !== "cancelled" && orderActiveRevenue(x) > 0);
  const cancelledOrders = orders.filter((x) => x.status === "cancelled" && orderActiveRevenue(x) > 0);
  // Net sales = total (incl. tax + service) of orders that have ANY revenue on
  // an active station. Orders exclusively on disabled stations are excluded.
  const sales = revenueOrders.reduce((a, x) => a + (x.total || 0), 0);
  const perStation = activeStations.map((s) => {
    const cats = new Set(s.assigned_categories || []);
    let tickets = 0, revenue = 0;
    for (const o of revenueOrders) {
      const matched = (o.items || []).filter((i) => cats.size === 0 || cats.has(i.category));
      const stationRev = matched.reduce((a, i) => a + (i.price || 0) * effQty(i), 0);
      if (stationRev > 0) {
        tickets += 1;
        revenue += stationRev;
      }
    }
    return { ...s, tickets, revenue };
  }).sort((a, b) => b.revenue - a.revenue);
  const top = perStation[0];
  const maxRev = Math.max(1, ...perStation.map((x) => x.revenue));

  const tokenMap = new Map();
  for (const o of orders) {
    if (o.token_number && o.status !== "served") tokenMap.set(o.token_number, o);
  }
  const inUse = tokenMap.size;

  return (
    <div className="workspace">
      <div className="page-head">
        <div>
          <p className="eyebrow">MANAGER OVERVIEW</p>
          <h1>Tonight at a glance</h1>
          <p className="muted">A quick read on service performance across all 15 stations</p>
        </div>
      </div>
      <div className="metrics">
        <div><span>Net sales</span><b>{money(sales)}</b><small className="green">{revenueOrders.length} paid ticket{revenueOrders.length === 1 ? "" : "s"}</small></div>
        <div><span>Tickets served</span><b>{revenueOrders.length}</b><small>{openOrders.length} open · {cancelledOrders.length} cancelled</small></div>
        <div><span>Tokens in use</span><b data-testid="tokens-in-use">{inUse}/50</b><small>{50 - inUse} available</small></div>
        <div data-testid="qr-scans-metric">
          <span>QR scans (display)</span>
          <b>{qrStats ? qrStats.scans_today : "—"}</b>
          <small>{qrStats ? `${qrStats.total_scans} all-time · ${qrStats.unique_scanners_7d} unique/7d` : "loading…"}</small>
        </div>
        <div data-testid="qr-scans-order-metric">
          <span>QR scans (order)</span>
          <b>{orderQrStats ? orderQrStats.scans_today : "—"}</b>
          <small>{orderQrStats ? `${orderQrStats.total_scans} all-time · ${orderQrStats.unique_scanners_7d} unique/7d` : "loading…"}</small>
        </div>
      </div>

      <div className="qr-double-row" data-testid="qr-double-row">
        <QrPosterPanel qrStats={qrStats} kind="display" title="Token display QR" subtitle="Print this &amp; place on tables — every scan is tracked" />
        <QrPosterPanel qrStats={orderQrStats} kind="order" title="Order menu QR" subtitle="Customers scan to browse the menu and place a draft" />
      </div>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="section-line"><h2>Token board</h2><span className="muted">Free tokens are grey · used tokens show status</span></div>
        <div className="token-grid" style={{ marginTop: 16 }}>
          {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => {
            const o = tokenMap.get(n);
            return (
              <div key={n} data-testid={`token-tile-${n}`} className={`token-tile ${o ? `used ${o.status}` : "free"}`}
                title={o ? `${o.table ? `Table ${o.table}` : (o.order_type === "table" ? "Dine-in" : "Takeaway")} · ${o.status}` : "Free"}>
                <b>{String(n).padStart(2, "0")}</b>
                {o && <small>{o.status}</small>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="manager-grid">
        <div className="panel">
          <div className="section-line"><h2>Station performance</h2><span className="muted">All time</span></div>
          {perStation.map((s) => (
            <div className="bar-row" key={s.id}>
              <span>{s.name}</span>
              <div><i style={{ width: `${(s.revenue / maxRev) * 100}%` }} /></div>
              <b>{money(s.revenue)}</b>
              <small>{s.tickets} tickets</small>
            </div>
          ))}
        </div>
        <div className="panel highlight">
          <p className="eyebrow">SERVICE NOTE</p>
          <h2>Keep the pass moving.</h2>
          <p>{top ? `${top.name} is leading with ${money(top.revenue)} across ${top.tickets} tickets. Consider moving one runner there during the next rush.` : "No tickets yet tonight — send a test order from POS to see stations light up."}</p>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="section-line">
          <h2>Discount audit</h2>
          <span className="muted" data-testid="discount-audit-total">
            {discountLog ? `${discountLog.count} tickets · ${money(discountLog.total_discount)} comped` : "loading…"}
          </span>
        </div>
        <AuditExportBar
          testPrefix="discount-export"
          start={dStart} end={dEnd}
          onStart={setDStart} onEnd={setDEnd}
          onDownload={() => downloadCsv("discounts", dStart, dEnd)}
        />
        {discountLog && discountLog.rows.length ? (
          <div className="report-table" style={{ marginTop: 12 }}>
            <div className="rt-head discount-head">
              <span>Order</span><span>When</span><span>Table</span><span>Subtotal</span><span>Discount</span><span>Reason</span>
            </div>
            {discountLog.rows.slice(0, 25).map((r) => (
              <div className="rt-row discount-row" key={r.order_id} data-testid={`discount-row-${r.order_id}`}>
                <b>#{r.order_number ?? r.order_id.slice(-8)}</b>
                <span>{new Date(r.created_at).toLocaleString()}</span>
                <em>{r.table || "—"}</em>
                <span>{money(r.subtotal)}</span>
                <strong className="green">-{money(r.discount)}</strong>
                <span className="reason-text">{r.discount_reason || "(no reason)"}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>No manual discounts recorded yet.</p>
        )}
      </div>

      <div className="panel" style={{ marginTop: 20 }}>
        <div className="section-line">
          <h2>Cancellations audit</h2>
          <span className="muted" data-testid="cancel-audit-total">
            {cancelLog ? `${cancelLog.count} events · ${money(cancelLog.total_amount)} voided` : "loading…"}
          </span>
        </div>
        <AuditExportBar
          testPrefix="cancel-export"
          start={cStart} end={cEnd}
          onStart={setCStart} onEnd={setCEnd}
          onDownload={() => downloadCsv("cancellations", cStart, cEnd)}
        />
        {cancelLog && cancelLog.rows.length ? (
          <div className="report-table" style={{ marginTop: 12 }}>
            <div className="rt-head cancel-head">
              <span>Type</span><span>When</span><span>Order</span><span>Token</span><span>Detail</span><span>Amount</span><span>Reason</span>
            </div>
            {cancelLog.rows.slice(0, 30).map((r) => (
              <div className="rt-row cancel-row" key={r.id} data-testid={`cancel-row-${r.id}`}>
                <b className={r.type === "order" ? "danger-tag" : "warn-tag"}>{r.type === "order" ? "ORDER" : "ITEM"}</b>
                <span>{new Date(r.created_at).toLocaleString()}</span>
                <em>#{r.order_number ?? r.order_id.slice(-8)}</em>
                <span>{r.token_number ? `T${String(r.token_number).padStart(2, "0")}` : "—"}</span>
                <span>
                  {r.type === "order"
                    ? `${(r.lines || []).length} line(s)`
                    : `${r.quantity}× ${r.item_name}`}
                </span>
                <strong className="green">-{money(r.amount)}</strong>
                <span className="reason-text">{r.reason || "(no reason)"}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>No cancellations recorded yet.</p>
        )}
      </div>
    </div>
  );
}

/* ---------- Menu Builder ---------- */
function QrPosterPanel({ qrStats, kind = "display", title = "Token display QR", subtitle = "" }) {
  const [imgOk, setImgOk] = useState(false);
  const url = qrStats?.url || "";
  const qrSrc = `${API}/qr/display.png?kind=${kind}&size=10`;

  const download = async () => {
    try {
      const r = await axios.get(`${API}/qr/display.png?kind=${kind}&size=14`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "image/png" });
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `qoh_${kind}_qr.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(dl), 500);
    } catch (_) { /* ignore */ }
  };

  const series = qrStats?.series || [];
  const maxVal = Math.max(1, ...series.map((s) => s.count));

  return (
    <div className="panel qr-panel" data-testid={`qr-poster-panel-${kind}`} style={{ marginBottom: 20 }}>
      <div className="section-line">
        <h2><QrCode size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />{title}</h2>
        <span className="muted">{subtitle}</span>
      </div>
      <div className="qr-grid">
        <div className="qr-image-wrap">
          <img
            src={qrSrc}
            alt={title}
            data-testid={`qr-image-${kind}`}
            onLoad={() => setImgOk(true)}
            onError={() => setImgOk(false)}
          />
          <button
            data-testid={`qr-download-button-${kind}`}
            className="outline"
            onClick={download}
            disabled={!imgOk}
            style={{ marginTop: 10 }}
          ><Download size={13} style={{ marginRight: 6 }} />Download PNG</button>
          <small className="muted" style={{ display: "block", marginTop: 6, wordBreak: "break-all", fontSize: 10 }}>
            {url}
          </small>
        </div>
        <div className="qr-stats-wrap">
          <div className="qr-stats-grid">
            <div><span>Today</span><b data-testid={`qr-scans-today-${kind}`}>{qrStats?.scans_today ?? "—"}</b></div>
            <div><span>Last 7 days</span><b>{qrStats?.scans_7d ?? "—"}</b></div>
            <div><span>Last 30 days</span><b>{qrStats?.scans_30d ?? "—"}</b></div>
            <div><span>All time</span><b>{qrStats?.total_scans ?? "—"}</b></div>
          </div>
          <div className="qr-chart" data-testid={`qr-chart-${kind}`} title="Scans by day">
            {series.map((d) => (
              <div key={d.date} className="qr-bar" title={`${d.date} — ${d.count} scans`}>
                <i style={{ height: `${(d.count / maxVal) * 100}%` }} />
                <em>{d.date.slice(-2)}</em>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function AuditExportBar({ testPrefix, start, end, onStart, onEnd, onDownload }) {
  const setToday = () => {
    const t = new Date().toISOString().slice(0, 10);
    onStart(t); onEnd(t);
  };
  const setLastN = (n) => {
    const e = new Date();
    const s = new Date(); s.setDate(s.getDate() - (n - 1));
    onStart(s.toISOString().slice(0, 10));
    onEnd(e.toISOString().slice(0, 10));
  };
  return (
    <div className="audit-export-bar" data-testid={`${testPrefix}-bar`} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 10, padding: 10, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--line)" }}>
      <span style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.8 }}>Range</span>
      <DateRangeField
        start={start} end={end}
        onChange={({ start: s, end: e }) => { onStart(s); onEnd(e); }}
        testPrefix={testPrefix}
      />
      <button type="button" onClick={setToday} data-testid={`${testPrefix}-today`} className="ghost" style={{ fontSize: 11, padding: "6px 10px" }}>Today</button>
      <button type="button" onClick={() => setLastN(7)} data-testid={`${testPrefix}-7d`} className="ghost" style={{ fontSize: 11, padding: "6px 10px" }}>Last 7d</button>
      <button type="button" onClick={() => setLastN(30)} data-testid={`${testPrefix}-30d`} className="ghost" style={{ fontSize: 11, padding: "6px 10px" }}>Last 30d</button>
      <div style={{ flex: 1 }} />
      <button
        type="button"
        className="primary"
        data-testid={`${testPrefix}-download`}
        onClick={onDownload}
        style={{ fontSize: 12, padding: "8px 14px" }}
      >Download CSV</button>
    </div>
  );
}
